import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/utils/api";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  Plane, 
  Hotel, 
  Ship, 
  Car, 
  Train,
  MapPin,
  Calendar,
  Building2,
  ArrowRight,
  Loader2,
  Command
} from "lucide-react";

const GlobalSearch = ({ open, onOpenChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ suggestions: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setQuery("");
      setResults({ suggestions: [] });
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 1) {
      setResults({ suggestions: [] });
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get(`/search/quick`, {
          params: { q: query }
        });
        setResults(response.data);
        setSelectedIndex(0);
      } catch (error) {
        setResults({ suggestions: [] });
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [query]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    const suggestions = results.suggestions || [];
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && suggestions.length > 0) {
      e.preventDefault();
      const selected = suggestions[selectedIndex];
      if (selected?.url) {
        navigate(selected.url);
        onOpenChange(false);
      }
    } else if (e.key === "Escape") {
      onOpenChange(false);
    }
  }, [results.suggestions, selectedIndex, navigate, onOpenChange]);

  const getIcon = (type, segmentType) => {
    if (type === "trip") return <Calendar className="h-4 w-4 text-sky-500" />;
    if (type === "vendor") return <Building2 className="h-4 w-4 text-purple-500" />;
    
    // Segment types
    switch (segmentType) {
      case "flight": return <Plane className="h-4 w-4 text-blue-500" />;
      case "accommodation": return <Hotel className="h-4 w-4 text-amber-500" />;
      case "cruise": return <Ship className="h-4 w-4 text-cyan-500" />;
      case "car_rental": return <Car className="h-4 w-4 text-green-500" />;
      case "train": return <Train className="h-4 w-4 text-orange-500" />;
      default: return <MapPin className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleSelect = (suggestion) => {
    if (suggestion?.url) {
      navigate(suggestion.url);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] p-0 gap-0 overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center border-b px-4 py-3">
          <Search className="h-5 w-5 text-gray-400 mr-3" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search trips, flights, hotels, confirmations..."
            className="border-0 focus-visible:ring-0 text-base placeholder:text-gray-400"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto">
          {query && results.suggestions?.length === 0 && !loading && (
            <div className="px-4 py-8 text-center text-gray-500">
              <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No results found for &quot;{query}&quot;</p>
              <p className="text-sm mt-1">Try searching by trip name, flight number, or confirmation</p>
            </div>
          )}

          {results.suggestions?.length > 0 && (
            <ul className="py-2">
              {results.suggestions.map((suggestion, index) => (
                <li key={`${suggestion.type}-${suggestion.id}`}>
                  <button
                    onClick={() => handleSelect(suggestion)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      index === selectedIndex 
                        ? "bg-sky-50 text-sky-900" 
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      {getIcon(suggestion.type, suggestion.segment_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{suggestion.title}</p>
                      <p className="text-sm text-gray-500 truncate">{suggestion.subtitle}</p>
                    </div>
                    <ArrowRight className={`h-4 w-4 flex-shrink-0 ${
                      index === selectedIndex ? "text-sky-500" : "text-gray-300"
                    }`} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!query && (
            <div className="px-4 py-6 text-center text-gray-500">
              <Command className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Start typing to search across your trips</p>
              <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-400">
                <span>↑↓ Navigate</span>
                <span>↵ Select</span>
                <span>Esc Close</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-2 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <span>
            <kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-600 font-mono">⌘</kbd>
            <kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-600 font-mono ml-1">K</kbd>
            <span className="ml-2">to search anytime</span>
          </span>
          {results.suggestions?.length > 0 && (
            <span>{results.suggestions.length} results</span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
